import React, { useState } from 'react';
import { AlertCircle, Landmark, Pencil, Plus, Trash2, X } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { CompanyBankAccount } from '../../../shared/lib/api';
import type { BankAccountPayload } from '../api';
import {
  useAtualizarContaBancaria,
  useContasBancarias,
  useCriarContaBancaria,
  useDeletarContaBancaria,
} from '../hooks';
import { normalizeBankAccount, validateBankAccount } from '../validation/companyForms';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'corrente', label: 'Conta Corrente' },
  { value: 'poupança', label: 'Conta Poupança' },
  { value: 'pagamento', label: 'Conta Pagamento' },
];

const emptyDraft = (): BankAccountPayload => ({
  bankName: '',
  bankAgency: '',
  bankAccount: '',
  accountType: '',
  pixKey: '',
});

interface Props {
  companyId: string;
}

export const BankAccountsSection: React.FC<Props> = ({ companyId }) => {
  const { data, isLoading, isError } = useContasBancarias(companyId);
  const createAccount = useCriarContaBancaria(companyId);
  const updateAccount = useAtualizarContaBancaria(companyId);
  const deleteAccount = useDeletarContaBancaria(companyId);
  const [form, setForm] = useState<BankAccountPayload>(emptyDraft());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof BankAccountPayload | 'submit', string>>>({});
  const accounts = data ?? [];
  const saving = createAccount.isPending || updateAccount.isPending;

  const closeForm = () => {
    setForm(emptyDraft());
    setEditingId(null);
    setShowForm(false);
    setErrors({});
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyDraft());
    setErrors({});
    setShowForm(true);
  };

  const startEdit = (account: CompanyBankAccount) => {
    setEditingId(account.id);
    setForm({
      bankName: account.bankName,
      bankAgency: account.bankAgency,
      bankAccount: account.bankAccount,
      accountType: account.accountType,
      pixKey: account.pixKey ?? '',
    });
    setErrors({});
    setShowForm(true);
  };

  const submit = async () => {
    const nextErrors = validateBankAccount(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      const payload = normalizeBankAccount(form);
      if (editingId) {
        await updateAccount.mutateAsync({ bankId: editingId, payload });
      } else {
        await createAccount.mutateAsync(payload);
      }
      closeForm();
    } catch (error: unknown) {
      const responseMessage = (error as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setErrors((current) => ({
        ...current,
        submit: Array.isArray(responseMessage)
          ? responseMessage.join(' ')
          : responseMessage || 'Não foi possível salvar a conta bancária.',
      }));
    }
  };

  const confirmDelete = async (accountId: string) => {
    try {
      await deleteAccount.mutateAsync(accountId);
      setPendingDeleteId(null);
      if (editingId === accountId) closeForm();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message;
      setErrors((current) => ({ ...current, submit: message || 'Não foi possível remover a conta bancária.' }));
    }
  };

  const setField = (field: keyof BankAccountPayload, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  return (
    <section className="space-y-3" aria-label="Informações Bancárias">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Informações Bancárias</h3>
          <p className="text-xs text-gray-500">Contas utilizadas em relatórios e controles financeiros.</p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={startCreate}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Adicionar Conta Bancária
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Carregando contas bancárias...</p>}
      {isError && <p className="text-sm text-red-600">Não foi possível carregar as contas bancárias.</p>}
      {errors.submit && !showForm && (
        <p className="flex items-center gap-1.5 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {errors.submit}
        </p>
      )}
      {!isLoading && !isError && accounts.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-600">
          Nenhuma conta bancária cadastrada.
        </div>
      )}

      {accounts.length > 0 && (
        <div className="space-y-2">
          {accounts.map((account) => (
            <div key={account.id} className="flex items-start justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-start gap-2">
                <Landmark className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{account.bankName}</p>
                  <p className="text-gray-500">
                    Ag. {account.bankAgency} · Conta {account.bankAccount} ·{' '}
                    {ACCOUNT_TYPE_OPTIONS.find((option) => option.value === account.accountType)?.label ?? account.accountType}
                  </p>
                  {account.pixKey && <p className="text-gray-500">Pix: {account.pixKey}</p>}
                </div>
              </div>
              <div className="ml-2 flex flex-shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => startEdit(account)}
                  aria-label={`Editar conta ${account.bankName}`}
                  className="rounded p-1 text-blue-600 hover:bg-blue-50"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                {pendingDeleteId === account.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => confirmDelete(account.id)}
                      disabled={deleteAccount.isPending}
                      className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      Confirmar exclusão
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteId(null)}
                      className="rounded p-1 text-gray-500 hover:bg-gray-100"
                      aria-label="Cancelar exclusão"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPendingDeleteId(account.id)}
                    aria-label={`Remover conta ${account.bankName}`}
                    className="rounded p-1 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-medium text-blue-900">{editingId ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}</p>
          <TextInput
            label="Banco *"
            icon={<Landmark className="h-5 w-5" />}
            value={form.bankName}
            onChange={(event) => setField('bankName', event.target.value)}
            error={errors.bankName}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              label="Agência *"
              value={form.bankAgency}
              onChange={(event) => setField('bankAgency', event.target.value)}
              error={errors.bankAgency}
              inputMode="numeric"
              maxLength={6}
            />
            <TextInput
              label="Conta *"
              value={form.bankAccount}
              onChange={(event) => setField('bankAccount', event.target.value)}
              error={errors.bankAccount}
              inputMode="numeric"
              maxLength={20}
            />
          </div>
          <AutocompleteSelect
            label="Tipo de Conta *"
            options={ACCOUNT_TYPE_OPTIONS}
            value={form.accountType}
            onChange={(value) => setField('accountType', value)}
            error={errors.accountType}
          />
          <TextInput
            label="Chave Pix (opcional)"
            value={form.pixKey ?? ''}
            onChange={(event) => setField('pixKey', event.target.value)}
          />
          {errors.submit && (
            <p className="flex items-center gap-1.5 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {errors.submit}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : editingId ? 'Salvar Conta' : 'Cadastrar Conta'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
